import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import {
  BatchType,
  PaymentBatch,
  PaymentBatchPayable,
  PaymentBatchSummary,
} from '../../Interfaces/Accounting/AccountsPayables.interfaces';
import { Company } from '../../Interfaces/admin/User.interfaces';

const apiUrl = process.env['NX_API_URL'];

export type fetchAccountsPayablesDataArgs = {
  company: Company;
  info?: string;
  dates?: Date[];
  vendors?: string[];
};

export const fetchAccountsPayablesList = async (
  companyID: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/accountsPayables/list`, {
    signal,
    params: {
      companyID,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchAccountsPayablesData = async (
  params: fetchAccountsPayablesDataArgs & {
    reportType: string;
    edit?: boolean;
    draftID?: string;
  },
  signal?: AbortSignal
) => {
  const { dates, company, ...otherParams } = params;
  let dateStart = null;
  let dateEnd = null;

  if (dates) {
    dateEnd = dates[1] || dates[0];
    dateStart = dates[0];
  }
  const cleanedParams = cleanJsonEmptyValues(otherParams);

  const response = await axios.get(`${apiUrl}/api/accountsPayables`, {
    signal,
    params: {
      ...cleanedParams,
      companyID: company.id,
      dateEnd,
      dateStart,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchAPOpenAged = async (
  params: fetchAccountsPayablesDataArgs,
  signal?: AbortSignal
) => {
  const { dates, company, ...otherParams } = params;
  let dateStart = null;
  let dateEnd = null;

  if (dates) {
    dateEnd = dates[1] || dates[0];
    dateStart = dates[0];
  }
  const cleanedParams = cleanJsonEmptyValues(otherParams);

  const response = await axios.get(`${apiUrl}/api/accountsPayables/openAged`, {
    signal,
    params: {
      ...cleanedParams,
      companyID: company.id,
      dateEnd,
      dateStart,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchAPOpenPerJob = async (
  params: fetchAccountsPayablesDataArgs & {
    jobs?: string[];
    edit?: boolean;
    draftID?: string;
  },
  signal?: AbortSignal
) => {
  const { dates, company, ...otherParams } = params;
  let dateStart = null;
  let dateEnd = null;

  if (dates) {
    dateEnd = dates[1] || dates[0];
    dateStart = dates[0];
  }
  const cleanedParams = cleanJsonEmptyValues(otherParams);

  const response = await axios.get(
    `${apiUrl}/api/accountsPayables/openPerJob`,
    {
      signal,
      params: {
        ...cleanedParams,
        companyID: company.id,
        dateEnd,
        dateStart,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const dashboard = response.data;
  return dashboard;
};

export const fetchAccountsPayablesResume = async (
  company: Company,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/accountsPayables/resume`, {
    signal,
    params: { companyID: company.id },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const fetchAPOpenJobs = async (
  company: Company,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/accountsPayables/openJobs`, {
    signal,
    params: { companyID: company.id },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const fetchSubmittedPaymentBatches = async (
  company: Company,
  type: BatchType,
  signal?: AbortSignal
): Promise<PaymentBatchSummary[]> => {
  const response = await axios.get(
    `${apiUrl}/api/accountsPayables/paymentBatch/submit`,
    {
      signal,
      params: { companyID: company.id, type },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export type fetchPayableDetailsArgs = {
  apref: string;
  aptrans: number;
  vendor: number;
  invDate: Date;
  company: Company;
};

export const fetchPayableDetails = async (
  params: fetchPayableDetailsArgs,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accountsPayables/payablesDetails`,
    {
      signal,
      params: {
        companyID: params.company.id,
        apref: params.apref,
        aptrans: params.aptrans,
        vendor: params.vendor,
        invDate: params.invDate,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchPayableAccountsBalances = async (
  company: Company,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accountsPayables/total-balances`,
    {
      signal,
      params: { companyID: company.id },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postPaymentBatch = async (
  company: Company,
  batch: PaymentBatchPayable[],
  type: BatchType
) => {
  const response = await axios.post(
    `${apiUrl}/api/accountsPayables/paymentBatch`,
    { batch, companyID: company.id, type },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const putPaymentBatch = async (
  id: PaymentBatch['id'],
  batch: PaymentBatchPayable[],
  fromApproval?: boolean
) => {
  const response = await axios.put(
    `${apiUrl}/api/accountsPayables/paymentBatch/${id}`,
    { batch, fromApproval },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const submitPaymentBatch = async (id: PaymentBatch['id']) => {
  const response = await axios.put(
    `${apiUrl}/api/accountsPayables/paymentBatch/submit/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const approvePaymentBatch = async (id: PaymentBatch['id']) => {
  const response = await axios.put(
    `${apiUrl}/api/accountsPayables/paymentBatch/approve/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const uploadPaymentBatch = async (id: PaymentBatch['id']) => {
  const response = await axios.put(
    `${apiUrl}/api/accountsPayables/paymentBatch/upload/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const rejectPaymentBatch = async (
  id: PaymentBatch['id'],
  reason: string
) => {
  const response = await axios.put(
    `${apiUrl}/api/accountsPayables/paymentBatch/reject/${id}`,
    { reason },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};
